import { isObject } from '@iconik-io/core/src/is_object';
import { isString } from '@iconik-io/core/src/is_string';

import {
  type CuttingRoomResponse,
  cuttingRoomBridge,
} from './cutting_room_bridge';

const readString = (value: unknown): string | undefined =>
  isString(value) ? value : undefined;

export type { CuttingRoomResponse };

/**
 * Send an `open_project` command to load a project in the embedded player.
 */
export function openProject(projectId: string): void {
  cuttingRoomBridge.post('open_project', { projectId });
}

export type ProjectBinItem = {
  url: string;
  resourceId?: string;
  title: string;
  contentType: string;
};

type CreateProjectPayload = {
  title: string;
  fps: number;
  projectId?: string;
  projectBinItems?: ProjectBinItem[];
};

type CreateProjectResponseData = {
  projectId: string;
};

/**
 * Add items to the currently open CuttingRoom project's bin via the
 * `add_to_project_bin` postMessage action.
 *
 * Throws if CuttingRoom reports anything but `ok`. No caller reads the rest
 * of the response, so it is not returned.
 */
export async function addToProjectBin(
  projectBinItems: (string | ProjectBinItem)[],
): Promise<void> {
  const { response } = await cuttingRoomBridge.send('add_to_project_bin', {
    projectBinItems,
  });

  if (readString(response.status) !== 'ok') {
    const error = readString(response.error);
    throw new Error(
      `Failed to add items to project bin: ${error ?? 'unknown error'}`,
    );
  }
}

/**
 * Create a new CuttingRoom project via the `call_api` postMessage action.
 *
 * Uses the CuttingRoom embedded `call_api` bridge which proxies the request
 * through the authenticated iframe session.
 *
 * @see https://docs.cuttingroom.com/reference/create-new-project.md
 * @see https://docs.cuttingroom.com/reference/calling-backend-api-on-behalf-of-logged-in-user.md
 */
export async function createProject(
  payload: CreateProjectPayload,
): Promise<CreateProjectResponseData> {
  const { response } = await cuttingRoomBridge.send('call_api', {
    path: '/projects',
    method: 'POST',
    body: payload,
  });

  const data = isObject(response.data) ? response.data : undefined;
  const projectId = data && readString(data.projectId);

  if (readString(response.status) !== 'ok' || !data || !projectId) {
    const error = readString(response.error);
    throw new Error(
      `CuttingRoom project creation failed: ${error ?? 'unknown error'}`,
    );
  }

  return { projectId };
}
